import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
// react-bootstrap
import {
  Container,
  Card,
  Col,
  Row,
  Image,
  ProgressBar,
  Badge,
  Modal,
  Button,
} from "react-bootstrap";
import { webServiceUrl } from "../../../components/apiCall/webserviceurl";
import axios from "axios";
import "./custom.css";
import "./admin.css";

import { Oval } from "react-loader-spinner";
import { useLocation, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
// ── React Icons used to replace emoji throughout the dashboard ──────────
import {
  MdEvent,
  MdNotifications,
  MdCheckCircle,
  MdAlarm,
  MdDateRange,
  MdCalendarToday,
  MdLocationOn,
  MdSchool,
  MdMenuBook,
  MdCampaign,
  MdPlayCircleFilled,
  MdDescription,
  MdFileDownload,
  MdVideoLibrary,
  MdWorkspacePremium,
  MdContacts,
  MdKeyboardArrowDown,
} from "react-icons/md";
import { FaChalkboardTeacher } from "react-icons/fa";
import RelievingLetterUpload from "../../../components/RelievingLetter";
import { useDispatch } from "react-redux";
import { isLeadData, setFeatureConfig } from "../../../store/setting/actions";

const modeVariant = {
  Online: "info",
  "In person": "warning",
};

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

// "09:30:00" -> "9:30 AM"
const formatTime = (t) => {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let hour = parseInt(hStr, 10);
  if (Number.isNaN(hour)) return "";
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${mStr} ${ampm}`;
};

// "09:30:00" + "13:00:00" -> "3h 30m"
const calcDuration = (start, end) => {
  if (!start || !end) return "";
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return "";
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h && !m) return "";
  return `${h ? h + "h " : ""}${m ? m + "m" : ""}`.trim();
};

// Converts one raw /lesson_plan record into the shape Program Detail /
// Today's Schedule / Session Resources all read from.
const mapSession = (s, idx) => ({
  unit: s?.unit ? s.unit.replace(/^UNIT\s*/i, "U") : `U${idx + 1}`,
  unitLabel: s?.unit || `UNIT ${idx + 1}`,
  title: s?.topic || s?.subunit_desc || s?.description || "Untitled Session",
  description: s?.description || s?.subunit_desc || "",
  timeStart: s?.start_time || "",
  timeEnd: s?.end_time || "",
  time_display:
    s?.start_time && s?.end_time
      ? `${formatTime(s.start_time)} - ${formatTime(s.end_time)}`
      : "",
  lead: s?.emp_name,
  duration: calcDuration(s?.start_time, s?.end_time) || "—",
  mode: s?.venue ? "In person" : "Online",
  venue: s?.venue || "",
  planned_on: s?.planned_on || "",
  ...s,
  // Raw resource links straight from the API, kept alongside so the
  // Session Resources card can use them once you're ready to wire it
  // fully to /lesson_plan instead of the separate resource endpoint.
  resources: {
    lesson_pdf_path: s?.lesson_pdf_path || "",
    session_recording: s?.session_recording || "",
    resources_zip: s?.resources_zip || "",
    worksheets: s?.worksheets || "",
    assignment: s?.assignment || "",
    tchr_material: s?.tchr_material || "",
  },
});

// Certificate / Relieving Letter preview — now takes participant /
// program / document data as PROPS instead of reading module-level
// static constants, so it always reflects whatever came back from the
// API for the logged-in user.
const CertificatePreview = React.forwardRef(
  ({ participantInfo, programInfo, documentsInfo }, ref) => (
    <div className="doc-preview doc-preview--certificate" ref={ref}>
      <div className="doc-preview-border">
        <div className="doc-preview-watermark">IHFC · IIT DELHI</div>
        <div className="doc-preview-header">
          <div className="doc-preview-crest">IHFC</div>
          <div>
            <div className="doc-preview-org">
              IHFC, Indian Institute of Technology Delhi
            </div>
            <div className="doc-preview-org-sub">
              Technology Innovation Hub in Robotics
            </div>
          </div>
        </div>

        <div className="doc-preview-eyebrow">Certificate of Completion</div>
        <div className="doc-preview-name">{participantInfo?.name}</div>
        <p className="doc-preview-body">
          has successfully completed the training program
          <br />
          <strong>{participantInfo?.program}</strong>
          <br />
          conducted at {programInfo?.location} during{" "}
          {programInfo?.dateOfTraining}.
        </p>

        <div className="doc-preview-footer">
          <div className="doc-preview-sign">
            <div className="doc-preview-sign-line" />
            <div className="doc-preview-sign-label">Program Coordinator</div>
          </div>
          <div className="doc-preview-meta">
            Certificate ID: {documentsInfo?.certificate?.certId}
          </div>
          <div className="doc-preview-sign">
            <div className="doc-preview-sign-line" />
            <div className="doc-preview-sign-label">Director, IHFC</div>
          </div>
        </div>
      </div>
      <div className="doc-preview-flag">Sample preview · not the final PDF</div>
    </div>
  ),
);

const LetterPreview = React.forwardRef(
  ({ participantInfo, programInfo, documentsInfo }, ref) => (
    <div className="doc-preview doc-preview--letter" ref={ref}>
      <div className="doc-preview-border doc-preview-border--letter">
        <div className="doc-preview-header">
          <div className="doc-preview-crest">IHFC</div>
          <div>
            <div className="doc-preview-org">
              IHFC, Indian Institute of Technology Delhi
            </div>
            <div className="doc-preview-org-sub">{programInfo?.location}</div>
          </div>
        </div>

        <div className="doc-preview-letter-meta">
          <span>Ref: {documentsInfo?.relievingLetter?.refNo}</span>
          <span>Date: {new Date().toLocaleDateString("en-IN")}</span>
        </div>

        <div className="doc-preview-letter-title">
          TO WHOMSOEVER IT MAY CONCERN
        </div>

        <p className="doc-preview-letter-body">
          This is to certify that <strong>{participantInfo?.name}</strong> was
          enrolled in the <strong>{participantInfo?.program}</strong> program (
          {participantInfo?.cohort}) conducted by IHFC at{" "}
          {participantInfo?.venue} during {programInfo?.dateOfTraining}.
        </p>
        <p className="doc-preview-letter-body">
          During this period, the participant's conduct and engagement with the
          program were found to be satisfactory. This letter is issued upon
          request for the purpose the participant may deem fit.
        </p>

        <div className="doc-preview-footer doc-preview-footer--letter">
          <div className="doc-preview-sign">
            <div className="doc-preview-sign-line" />
            <div className="doc-preview-sign-label">
              {programInfo?.coordinators?.[0]?.name}
            </div>
            <div className="doc-preview-sign-sub">
              Program Coordinator, IHFC
            </div>
          </div>
        </div>
      </div>
      <div className="doc-preview-flag">Sample preview · not the final PDF</div>
    </div>
  ),
);

const getYouTubeEmbedUrl = (url) => {
  if (!url) return url;
  try {
    if (url.includes("youtube.com/embed/")) return url;
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    const videoId = u.searchParams.get("v");
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    return url;
  } catch (e) {
    return url;
  }
};

// Identity payload used by /getLessonPlanDays and /lesson_plan. This is
// REQUEST configuration (what to ask the API for), not display data, so
// it stays — but school_code is pulled live from the selected course
// (see getSchoolCode) rather than hardcoded.
const LESSON_PLAN_IDENTITY = {
  class_name: "ALL",
  section: "A",
  level: "School Teachers",
  combination_code: "AIML",
};

const Admin = () => {
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [data, setData] = useState([]);
  const [apierror, setApiError] = useState(false);
  const navigate = useNavigate();
  let token = localStorage.getItem("token");
  const [lessPlan, setlessPlan] = useState({
    class_name: "",
    section: "",
    level: "",
    combination_code: "",
  });

  const isLessonPlanReady =
    !!lessPlan?.class_name &&
    !!lessPlan?.section &&
    !!lessPlan?.level &&
    !!lessPlan?.combination_code;


  const users = JSON.parse(localStorage.getItem("users"));
  const [isLead, setIsLead] = useState(() => {
    return localStorage.getItem("is_lead");
  });
  const location = useLocation();
  useEffect(() => {
    const leadValue = localStorage.getItem("is_lead");
    setIsLead(leadValue);
  }, [location.pathname]);


  const {
    is_post_feedback_form_disabled,
    is_top_submission_hidden,
    is_relieving_letter_enabled,
  } = useSelector((state) => state.setting.setting);

  // Quick tiles now start empty and are populated entirely from
  // /get_dashboard_details (see getDashBoard below).
  const [quickTiles, setquickTiles] = useState([
    {
      icon: <MdEvent />,
      label: "Day of Training",
      value: "",
      suffix: "",
      tone: "bg-soft-primary",
    },
    {
      icon: <MdNotifications />,
      label: "Notifications",
      value: "",
      suffix: "",
      tone: "bg-soft-warning",
    },
    {
      icon: <MdCheckCircle />,
      label: "Attendance",
      value: "",
      suffix: "",
      tone: "bg-soft-success",
    },
    {
      icon: <MdAlarm />,
      label: "TOP Submission Deadline",
      value: "",
      suffix: "",
      tone: "bg-soft-danger",
    },
  ]);

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const toggleMobileMenu = () => {
    setShowMobileMenu((prev) => !prev);
    document.body.classList.toggle("sidebar-close");
  };

  // Which "Program Detail" day is active, driven by the dropdown. Starts
  // empty until /getLessonPlanDays resolves.
  const [activeDay, setActiveDay] = useState("");

  const [sessionResources, setsessionResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);

  const getSessionRecoruse = async (day) => {
    if (!day) return;
    try {
      setResourcesLoading(true);
      let response = await axios.post(
        webServiceUrl.fetch_session_resource,
        {
          day,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.success) {
        setsessionResources(response?.data?.response || []);
      } else {
        setsessionResources([]);
      }
    } catch (e) {
      console.log(e, "error");
      setsessionResources([]);
    } finally {
      setResourcesLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // PROGRAM DETAIL DAYS — /getLessonPlanDays
  // ---------------------------------------------------------------------
  const [programDaysList, setProgramDaysList] = useState([]);
  const [daysLoading, setDaysLoading] = useState(true);

  const activeDayData = programDaysList.find((d) => d.id === activeDay) ||
    programDaysList[0] || { label: "", date: "", sessions: [] };

  const getLessonPlanDays = async () => {
    try {
      setDaysLoading(true);
      const res = await axios.post(webServiceUrl.getLessonPlanDays, lessPlan, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawDays = res?.data?.data || res?.data?.days || [];

      if (Array.isArray(rawDays) && rawDays.length > 0) {
        const mappedDays = rawDays.map((day, idx) => ({
          id: day?.id || day?.day_id || day?.day || `day-${idx + 1}`,
          label: day?.label || day?.day_name || day?.day || `Day ${idx + 1}`,
          date:
            day?.display_date || day?.date || day?.planned_on || day?.day || "",
          planned_on: day?.planned_on || day?.date || day?.day || "",
          sessions: [],
          daydisplay: day?.daydisplay || `Day ${idx + 1}`,
          ...day,
        }));
        setProgramDaysList(mappedDays);
        setActiveDay(mappedDays[0].id);
      } else {
        setProgramDaysList([]);
      }
    } catch (e) {
      console.log(e, "ERROR coming from getLessonPlanDays");
      setProgramDaysList([]);
    } finally {
      setDaysLoading(false);
    }
  };

  // ---------------------------------------------------------------------
  // PROGRAM DETAIL SESSIONS + TODAY'S SCHEDULE — /lesson_plan
  // ---------------------------------------------------------------------
  const [todayScheduleList, setTodayScheduleList] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const getSchoolCode = () => {
    try {
      const storedCourse = JSON.parse(localStorage.getItem("course"));
      return storedCourse?.school_code || "";
    } catch (e) {
      return "";
    }
  };

  const getLessonPlan = async (plannedOn) => {
    if (!plannedOn) return [];
    try {
      const payload = {
        ...lessPlan,
        planned_on: plannedOn,
        school_code: getSchoolCode(),
      };
      const res = await axios.post(webServiceUrl.lesson_plan, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawSessions = res?.data?.data || res?.data?.lessonPlan || [];
      return Array.isArray(rawSessions) ? rawSessions : [];
    } catch (e) {
      console.log(e, "ERROR coming from getLessonPlan");
      return [];
    }
  };

  // Fetch sessions (+ resources) for whichever day is selected in the
  // dropdown. Raw API records are run through mapSession() before being
  // stored, so session.title / session.lead / session.duration /
  // session.time_display all render correctly.
  useEffect(() => {
    const dayObj = programDaysList.find((d) => d.id === activeDay);
    if (!dayObj?.planned_on)
      return


    const fetchSessionsForDay = async () => {
      try {
        setSessionsLoading(true);
        const rawSessions = await getLessonPlan(dayObj.planned_on);
        const mapped = rawSessions.map(mapSession);
        setProgramDaysList((prev) =>
          prev.map((d) =>
            d.id === activeDay ? { ...d, sessions: mapped } : d,
          ),
        );
        if (mapped.length > 0) {
          getSessionRecoruse(dayObj.planned_on);
        } else {
          setsessionResources([]);
        }
      } catch (e) {
        console.log(e, "ERROR fetching sessions for day");
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchSessionsForDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDay, daysLoading]);

  // Fetch today's sessions independently for the "Today's Schedule" panel
  // — not tied to whichever day is selected in the dropdown above.
  const getTodaySchedule = async () => {
    const todayDate = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
    const rawSessions = await getLessonPlan(todayDate);
    setTodayScheduleList(
      rawSessions.length > 0 ? rawSessions.map(mapSession) : [],
    );
  };

  // console.log(data, "pressings data<<<<<<<<<<<<");

  const getEnrolledCourses = async () => {
    // const userId = localStorage.getItem("userId");
    try {
      setPageLoading(true);
      const response = await axios.post(
        webServiceUrl.enrolledCourses,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setData(response.data.enrolledCourses);
      let res = response.data.enrolledCourses[0];
      localStorage.setItem(
        "course_co",
        JSON.stringify(response.data.enrolledCourses[0]),
      );
      setlessPlan({
        class_name: res?.class,
        section: res?.section,
        level: res?.level,
        combination_code: res?.combination_code,
      });
    } catch (error) {
      console.log(error, "ERROR coming From  getEnrolledCourses ");
      setData([]);
      setApiError(true);
    } finally {
      setIsLoading(false);
      setPageLoading(false);
      setLoading(false);
    }
  };
  const dispatch = useDispatch();

  const getisLeadApi = async () => {
    try {
      let res = await axios.get(webServiceUrl.is_Lead, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // if (res?.data?.success) {
      //   localStorage.setItem("is_lead", res?.data?.isLead);
      //   localStorage.setItem("group_id", res?.data?.group_id);
      //   dispatch(isLeadData(res?.data?.isLead));
      // }

      if (res?.data?.success) {

        // Existing Group Lead configuration
        localStorage.setItem("is_lead", res.data.isLead);
        localStorage.setItem("group_id", res.data.group_id ?? "");

        dispatch(isLeadData(res.data.isLead));

        // New feature configuration
        const featureConfig = {
          is_post_feedback_form_disabled:
            Boolean(res.data.is_post_feedback_form_disabled ?? 0),

          is_top_submission_hidden:
            Boolean(res.data.is_top_submission_hidden ?? 0),

          is_relieving_letter_enabled:
            Boolean(res.data.is_relieving_letter_enabled ?? 0),
        };

        // Store in localStorage
        localStorage.setItem(
          "dashboardFeatureConfig",
          JSON.stringify(featureConfig)
        );

        // Store in Redux
        dispatch(setFeatureConfig(featureConfig));
      }

      if (res?.data?.statusCode === 401) {
        localStorage.removeItem("token")
      }
    } catch (e) {
      console.log(e);

      if (e?.response?.status === 401) {
        logout();
      }
    };
  }

  useLayoutEffect(() => {
    getisLeadApi();
    getEnrolledCourses();
  }, []);

  const [announcementsList, setAnnouncementsList] = useState([]);

  const getNotificationsList = async () => {
    try {
      const res = await axios.post(
        webServiceUrl.getNotifications,
        {
          users,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const rawNotifications =
        res?.data?.data || res?.data?.notifications || [];

      if (Array.isArray(rawNotifications) && rawNotifications.length > 0) {
        const mapped = rawNotifications.map((n) => ({
          title: n?.title || n?.message || "",
          date: n?.date || n?.created_at || "",
        }));
        setAnnouncementsList(mapped);
      } else {
        setAnnouncementsList([]);
      }
    } catch (e) {
      console.log(e, "ERROR coming from getNotifications");
      setAnnouncementsList([]);
    }
  };

  const school_code = localStorage.getItem("school_code");

  const school_code_en = school_code
    ? btoa(school_code)
    : null;

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("is_lead");
    localStorage.removeItem("group_id");

    navigate(
      `/auth/sign-in?school_code=${encodeURIComponent(school_code_en || "")}`
    );
  };

  // ---------------------------------------------------------------------
  // CERTIFICATE / RELIEVING LETTER — assumed endpoint. Rename
  // `webServiceUrl.get_documents_status` to whatever your real API is.
  // ---------------------------------------------------------------------
  const [documentsInfo, setDocumentsInfo] = useState({
    certificate: { status: "", label: "Certificate", url: "", certId: "" },
    relievingLetter: {
      status: "",
      label: "Relieving Letter",
      url: "",
      refNo: "",
    },
  });

  /*
  const getDocumentsInfo = async () => {
    try {
      const res = await axios.get(webServiceUrl.get_documents_status, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res?.data?.success) {
        const resp = res?.data?.data || {};
        setDocumentsInfo({
          certificate: {
            status: resp?.certificate?.status || "",
            label: "Certificate",
            url: resp?.certificate?.url || "",
            certId: resp?.certificate?.certId || "",
          },
          relievingLetter: {
            status: resp?.relievingLetter?.status || "",
            label: "Relieving Letter",
            url: resp?.relievingLetter?.url || "",
            refNo: resp?.relievingLetter?.refNo || "",
          },
        });
      }
    } catch (e) {
      console.log(e, "ERROR coming from getDocumentsInfo");
    }
  };*/


  useEffect(() => {
    if (isLessonPlanReady) {
      getLessonPlanDays();
      getTodaySchedule();
    }
    getNotificationsList();
    // getDocumentsInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessPlan]);

  const scrollRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const interval = setInterval(() => {
      if (
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth
      ) {
        container.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        container.scrollBy({ left: 320, behavior: "smooth" });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  const [selectCourse, setselectCourse] = useState(false);

  const enrolledOnly = (data || []).filter((course) => !!course?.student);

  const [dashProfile, setdashProfile] = useState({});
  const getDashBoard = async () => {
    try {
      let res = await axios.get(webServiceUrl.get_dashboard_details, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res?.data?.success) {
        let resp = res?.data?.data;
        setquickTiles([
          {
            icon: <MdEvent />,
            label: "Day of Training",
            value: resp?.dayOfTraining?.current,
            suffix: `/ ${resp?.dayOfTraining?.total}`,
            tone: "bg-soft-primary",
          },
          {
            icon: <MdNotifications />,
            label: "Notifications",
            value: resp?.notifications?.new,
            suffix: "new",
            tone: "bg-soft-warning",
          },
          {
            icon: <MdCheckCircle />,
            label: "Attendance",
            value: resp?.attendance?.present,
            suffix: `/ ${resp?.attendance?.total} days`,
            tone: "bg-soft-success",
          },
          {
            icon: <MdAlarm />,
            label: "TOP Submission Deadline",
            value: resp?.topSubmission,
            suffix: "",
            tone: "bg-soft-danger",
          },
        ]);
        setdashProfile(res?.data?.data);
      }
    } catch (e) {
      console.log(e, "ERROR");
    }
  };

  useEffect(() => {
    getDashBoard();
  }, []);

  // Program info and participant info are now DERIVED live from
  // dashProfile (the /get_dashboard_details response) on every render,
  // instead of coming from a static module-level object.
  const programInfo = {
    trainingName: dashProfile?.studentName || "",
    dateOfTraining: dashProfile?.dateOfTraining?.display || "",
    location: dashProfile?.location || "",
    coordinators: dashProfile?.coordinators || [],
  };

  const participantInfo = {
    name: dashProfile?.studentName || "",
    cohort: dashProfile?.cohort || "",
    program: dashProfile?.program || dashProfile?.programName || "",
    venue: dashProfile?.location || "",
  };

  const CourseTile = ({ course }) => {
    const [imgLoading, setImgLoading] = useState(true);
    localStorage.setItem("course", JSON.stringify(course));
    const hasLessonPlans = course?.student?.lessonPlanCount !== 0;
    const displayName = course?.school_name
      ?.toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    return (
      <div className="course-tile" onClick={() => setselectCourse(true)}>
        <div className="course-tile-inner">
          <div className="course-tile-face course-tile-front">
            <div className="course-tile-logo-wrap">
              {imgLoading && <Skeleton circle height={50} width={50} />}
              <img
                src={course?.logo_image}
                className="course-tile-logo"
                alt={course?.school_name || "course"}
                style={{ display: imgLoading ? "none" : "block" }}
                onLoad={() => setImgLoading(false)}
              />
            </div>

            <div className="course-tile-body">
              <div className="course-tile-name">{displayName}</div>

              {hasLessonPlans ? (
                <span className="course-tile-badge course-tile-badge--active">
                  <MdCheckCircle
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  {course?.student?.lessonPlanCount} plan
                  {course?.student?.lessonPlanCount === 1 ? "" : "s"}
                </span>
              ) : (
                <span className="course-tile-badge course-tile-badge--muted">
                  No plans yet
                </span>
              )}
            </div>
          </div>

          <div className="course-tile-face course-tile-back">
            <div className="course-tile-back-name">{displayName}</div>
            <div className="course-tile-back-row">
              <span className="course-tile-back-label">Student name</span>
              <span className="course-tile-back-value">
                {course?.student?.student_name ?? 0}
              </span>
            </div>
            <div className="course-tile-back-row">
              <span className="course-tile-back-label">Lesson plans</span>
              <span className="course-tile-back-value">
                {course?.student?.lessonPlanCount ?? 0}
              </span>
            </div>
            {course?.school_code && (
              <div className="course-tile-back-row">
                <span className="course-tile-back-label">Course code</span>
                <span className="course-tile-back-value">
                  {course.school_code}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState({
    kind: null,
    url: "",
    title: "",
    downloadUrl: "",
    downloadName: "",
  });
  const previewRef = useRef(null);

  const closeModal = () => {
    setModalShow(false);
    setModalContent({
      kind: null,
      url: "",
      title: "",
      downloadUrl: "",
      downloadName: "",
    });
  };

  const triggerDownload = (url, filename) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printPreview = (title) => {
    if (!previewRef.current) return;
    const printWindow = window.open("", "_blank", "width=850,height=1000");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>${title || "Document"}</title>
          <style>
            body { margin: 0; padding: 24px; font-family: Georgia, 'Times New Roman', serif; background: #fff; }
          </style>
        </head>
        <body>${previewRef.current.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleResourceAction = (res) => {
    if (!res?.url) return;

    if (res.type === "download") {
      triggerDownload(res.url, res.title || "resource");
      return;
    }

    if (res.type === "video") {
      setModalContent({
        kind: "video",
        url: getYouTubeEmbedUrl(res.url),
        title: res.title,
        downloadUrl: "",
        downloadName: "",
      });
      setModalShow(true);
      return;
    }

    setModalContent({
      kind: "pdf",
      url: res.url,
      title: res.title,
      downloadUrl: res.url,
      downloadName: res.title || "resource",
    });
    setModalShow(true);
  };

  // Documents now come purely from the API-backed `documentsInfo` state.
  // If a real file URL exists, open it as a PDF; otherwise fall back to
  // the live-data template preview (name/certId/refNo still pulled from
  // the API responses, nothing hardcoded).
  const handleDocumentView = (doc, kind) => {
    if (!doc?.url) {
      setModalContent({
        kind,
        url: "",
        title: doc?.label,
        downloadUrl: "",
        downloadName: doc?.label || "document",
      });
      setModalShow(true);
      return;
    }

    setModalContent({
      kind: "pdf",
      url: doc.url,
      title: doc.label,
      downloadUrl: doc.url,
      downloadName: doc.label || "document",
    });
    setModalShow(true);
  };

  return (
    <div id="content-page" className="content-inner dashboard pt-0 pt-md-4">
      <style>{`
          .mobile-menu-btn {
            display: none;
          }
          @media (max-width: 768px) {
            .mobile-menu-btn {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 42px;
              height: 42px;
              border-radius: 10px;
              border: 1px solid #dfe7e2;
              background: #ffffff;
              color: #1f7d38;
              box-shadow: 0 2px 8px rgba(20, 100, 45, 0.08);
            }
            .mobile-menu-btn:active {
              background: #eafaef;
            }
            .mobile-menu-bar {
              position: sticky;
              top: 0;
              z-index: 20;
              display: flex;
              align-items: center;
              padding: 10px 12px;
              background: #fbfdfb;
            }
          }

          .resource-btn:disabled,
          .cert-download-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }

          @media (max-width: 480px) {
            .resource-row {
              flex-direction: column;
              align-items: flex-start;
            }
            .resource-row-left {
              width: 100%;
            }
            .resource-btn {
              width: 100%;
              margin-top: 8px;
            }
          }

          @media (max-width: 380px) {
            .cert-row {
              padding: 0.85rem;
            }
            .cert-title {
              font-size: 0.88rem;
            }
            .cert-download-btn {
              width: 100%;
            }
          }

          /* ---------------- Day Selector Dropdown ---------------- */
          .day-select-wrap {
            position: relative;
            min-width: 190px;
          }
          .day-select-wrap select {
            appearance: none;
            -webkit-appearance: none;
            -moz-appearance: none;
            width: 100%;
            background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
            color: #fff;
            font-weight: 700;
            font-size: 0.85rem;
            letter-spacing: 0.01em;
            border: none;
            border-radius: 10px;
            padding: 9px 34px 9px 14px;
            cursor: pointer;
            box-shadow: 0 4px 12px rgba(21, 128, 61, 0.25);
            transition: box-shadow 0.2s ease, transform 0.15s ease;
          }
          .day-select-wrap select:hover {
            box-shadow: 0 6px 16px rgba(21, 128, 61, 0.35);
          }
          .day-select-wrap select:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.25);
          }
          .day-select-wrap select:active {
            transform: scale(0.98);
          }
          .day-select-wrap select option {
            color: #14532d;
            background: #ffffff;
            font-weight: 600;
          }
          .day-select-icon {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            color: #fff;
            pointer-events: none;
            display: flex;
            align-items: center;
          }
          .day-select-loading {
            position: absolute;
            right: 34px;
            top: 50%;
            transform: translateY(-50%);
          }

          /* ---------------- Preview Modal (video + PDF) ---------------- */
          .preview-modal-title {
            font-size: 1.05rem;
            font-weight: 700;
            color: #1e293b;
          }
          .preview-modal-body {
            padding: 0;
          }
          .preview-video-wrap {
            position: relative;
            width: 100%;
            padding-top: 56.25%;
            background: #000;
          }
          .preview-video-wrap iframe {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            border: 0;
          }
          .preview-pdf-wrap {
            width: 100%;
            height: 75vh;
          }
          .preview-pdf-wrap iframe {
            width: 100%;
            height: 100%;
            border: 0;
          }
          .preview-modal-download-btn {
            background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
            border: none;
          }
          .preview-modal-download-btn:hover {
            background: linear-gradient(135deg, #15803d 0%, #166534 100%);
          }

          @media (max-width: 767px) {
            .preview-modal-dialog {
              margin: 0.75rem;
            }
            .preview-pdf-wrap {
              height: 65vh;
            }
          }
          @media (max-width: 420px) {
            .preview-pdf-wrap {
              height: 55vh;
            }
          }

          /* ---------------- Dummy Certificate / Letter preview ---------------- */
          .preview-doc-wrap {
            max-height: 75vh;
            overflow-y: auto;
            background: #eef2f6;
            padding: 24px;
          }
          .preview-modal-footer--dummy {
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
          }
          .preview-modal-footer-note {
            font-size: 0.76rem;
            color: #94a3b8;
          }

          .doc-preview {
            position: relative;
            background: #ffffff;
            font-family: Georgia, "Times New Roman", serif;
            color: #1e293b;
            max-width: 640px;
            margin: 0 auto;
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
          }
          .doc-preview-flag {
            text-align: center;
            font-family: inherit;
            font-size: 0.7rem;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: #94a3b8;
            margin-top: 10px;
          }
          .doc-preview-border {
            position: relative;
            border: 2px solid #16a34a;
            outline: 1px solid #dcecdf;
            outline-offset: 6px;
            padding: 32px 28px;
            overflow: hidden;
          }
          .doc-preview-border--letter {
            outline: none;
            border: 1px solid #dbe4f0;
          }
          .doc-preview-watermark {
            position: absolute;
            top: 45%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-22deg);
            font-size: 2.6rem;
            font-weight: 800;
            color: rgba(22, 163, 74, 0.06);
            white-space: nowrap;
            pointer-events: none;
          }
          .doc-preview-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 18px;
          }
          .doc-preview-crest {
            width: 42px;
            height: 42px;
            border-radius: 50%;
            background: linear-gradient(135deg, #16a34a, #15803d);
            color: #fff;
            font-family: inherit;
            font-weight: 700;
            font-size: 0.72rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .doc-preview-org {
            font-size: 0.82rem;
            font-weight: 700;
            line-height: 1.2;
          }
          .doc-preview-org-sub {
            font-size: 0.7rem;
            color: #64748b;
          }
          .doc-preview-eyebrow {
            text-align: center;
            font-size: 0.72rem;
            letter-spacing: 0.14em;
            text-transform: uppercase;
            color: #16a34a;
            font-weight: 700;
            margin-top: 8px;
          }
          .doc-preview-name {
            text-align: center;
            font-size: 1.6rem;
            font-weight: 700;
            margin: 10px 0 16px;
            color: #14532d;
          }
          .doc-preview-body {
            text-align: center;
            font-size: 0.92rem;
            line-height: 1.7;
            color: #334155;
            margin-bottom: 24px;
          }
          .doc-preview-footer {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            gap: 12px;
            margin-top: 28px;
          }
          .doc-preview-footer--letter {
            justify-content: flex-start;
          }
          .doc-preview-meta {
            font-size: 0.68rem;
            color: #94a3b8;
            align-self: center;
          }
          .doc-preview-sign {
            text-align: center;
            min-width: 140px;
          }
          .doc-preview-sign-line {
            border-top: 1px solid #94a3b8;
            margin-bottom: 6px;
          }
          .doc-preview-sign-label {
            font-size: 0.76rem;
            font-weight: 700;
          }
          .doc-preview-sign-sub {
            font-size: 0.7rem;
            color: #64748b;
          }
          .doc-preview-letter-meta {
            display: flex;
            justify-content: space-between;
            font-size: 0.76rem;
            color: #64748b;
            margin-bottom: 22px;
          }
          .doc-preview-letter-title {
            text-align: center;
            font-weight: 700;
            letter-spacing: 0.06em;
            font-size: 0.9rem;
            margin-bottom: 18px;
          }
          .doc-preview-letter-body {
            font-size: 0.88rem;
            line-height: 1.7;
            color: #334155;
            margin-bottom: 14px;
            text-align: justify;
          }

          @media (max-width: 480px) {
            .doc-preview-border {
              padding: 22px 16px;
            }
            .doc-preview-name {
              font-size: 1.25rem;
            }
            .doc-preview-footer {
              flex-direction: column;
              align-items: flex-start;
              gap: 14px;
            }
          }
            .day-dropdown {
  min-width: 200px;
  height: 44px;
  padding: 0 16px;

  /* Enhanced border */
  border: 1.5px solid #d1d5db;
  border-radius: 10px;

  background: #fff;
  color: #333;

  font-size: 14px;
  font-weight: 500;

  outline: none;
  cursor: pointer;

  /* Border depth */
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.05),
    inset 0 0 0 1px rgba(255, 255, 255, 0.8);

  transition: all 0.25s ease;
}

/* Hover */
.day-dropdown:hover {
  border-color: #ff3b3b;

  box-shadow:
    0 0 0 3px rgba(255, 59, 59, 0.08),
    0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Focus */
.day-dropdown:focus {
  border-color: #ff3b3b;

  box-shadow:
    0 0 0 3px rgba(255, 59, 59, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Options */
.day-dropdown option {
  padding: 10px;
}
        `}</style>

      <Container fluid>
        <Row className="justify-content-center">
          <Col lg="12">
            {loading ? (
              <div
                className="d-flex justify-content-center "
                style={{ height: "85vh", paddingTop: "15vh" }}
              >
                <Oval
                  height="50"
                  width="50"
                  color="#4fa94d"
                  secondaryColor="#4fa94d"
                />
              </div>
            ) : (
              <Card.Body className="pt-5 px-1 px-md-2 mt-4">
                <section
                  className="container mb-4"
                  id="program-banner"
                  style={{ marginTop: "-30px" }}
                >
                  <Card className="program-banner-card p-4 p-md-5">
                    <div className="program-banner-eyebrow">
                      Program Dashboard
                    </div>
                    <h2
                      className="program-banner-title"
                      style={{ color: "white" }}
                    >
                      {dashProfile?.studentName}
                    </h2>
                    <div className="program-banner-meta">
                      <div className="program-banner-meta-item">
                        <span className="program-banner-meta-icon">
                          <MdDateRange />
                        </span>
                        <div>
                          <div className="program-banner-meta-label">
                            Date of Training
                          </div>
                          <div className="program-banner-meta-value">
                            {dashProfile?.dateOfTraining?.start} -{" "}
                            {dashProfile?.dateOfTraining?.end}
                          </div>
                        </div>
                      </div>
                      {dashProfile?.location && (
                        <div className="program-banner-meta-item">
                          <span className="program-banner-meta-icon">
                            <MdLocationOn />
                          </span>
                          <div>
                            <div className="program-banner-meta-label">
                              Location
                            </div>
                            <div className="program-banner-meta-value">
                              {dashProfile?.location}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="program-banner-meta-item">
                        <span className="program-banner-meta-icon">
                          <FaChalkboardTeacher />
                        </span>
                        <div>
                          <div className="program-banner-meta-label">
                            Program Coordinator
                            {dashProfile?.coordinators?.length > 1 ? "s" : ""}
                          </div>
                          <div className="program-banner-meta-value">
                            {dashProfile?.coordinators
                              ?.map((c) => c?.name)
                              .join(", ")}
                          </div>
                        </div>
                      </div>
                      <div className="program-banner-meta-item">
                        <span className="program-banner-meta-icon">
                          <MdContacts />
                        </span>
                        <div>
                          <div className="program-banner-meta-label">
                            Contact Number
                            {dashProfile?.coordinators?.length > 1 ? "s" : ""}
                          </div>
                          <div className="program-banner-meta-value">
                            {dashProfile?.coordinators
                              ?.map((c) => c?.msisdn)
                              .join(", ")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </section>

                <section className="container mb-5" id="quick-tiles">
                  <div className="quick-tile-row">
                    {quickTiles.map((tile, idx) => (
                      <div
                        className="dash-stat-card"
                        key={idx}
                        onClick={() => {
                          if (tile.label == "TOP Submission Deadline") {
                            if (isLead == "1") {
                              navigate("TOP");
                            }
                          } else if (tile.label == "Notifications") {
                            navigate("/dashboard/app/notification");
                          }
                        }}
                      >
                        <span className={`dash-icon-circle ${tile.tone}`}>
                          {tile.icon}
                        </span>
                        <div>
                          <div className="dash-stat-value">
                            {tile.value}{" "}
                            {tile.suffix && (
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: 600,
                                  color: "#9ca3af",
                                }}
                              >
                                {tile.suffix}
                              </span>
                            )}
                          </div>
                          <div className="dash-stat-label">{tile.label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {enrolledOnly.length > 0 && (
                  <section className="container mb-5" id="enrolled-courses">
                    <Card className="dash-card">
                      <Card.Header className="bg-white border-0 d-flex align-items-center justify-content-between pt-4 px-4">
                        <div className="d-flex align-items-center">
                          <span className="dash-icon-circle bg-soft-info me-2">
                            <MdSchool />
                          </span>
                          <h5 className="card-title mb-0">
                            Your Enrolled Courses
                          </h5>
                        </div>
                        <Badge bg="light" text="dark" className="border">
                          {enrolledOnly.length} enrolled
                        </Badge>
                      </Card.Header>
                      <Card.Body className="px-4 pb-4 pt-2">
                        <div
                          ref={scrollRef}
                          style={{
                            display: "flex",
                            overflowX: "auto",
                            gap: "12px",
                            scrollBehavior: "smooth",
                          }}
                        >
                          {enrolledOnly.map((course, idx) => (
                            <div
                              key={course?.school_code || idx}
                              style={{ flex: "0 0 auto" }}
                            >
                              <CourseTile course={course} />
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </section>
                )}

                {is_relieving_letter_enabled && (
                  <RelievingLetterUpload />
                )}

                {/* ============================================================
                      PROGRAM DETAIL (left)  ·  TODAY'S SCHEDULE (right)
                      Program Detail uses a styled dropdown to pick the day.
                      Selecting a day re-hits /lesson_plan for that day's
                      sessions AND re-hits the Session Resources endpoint
                      for the same day.
                  ============================================================ */}
                <section className="container mb-5" id="dashboard-overview">
                  <Row className="g-4 align-items-stretch">
                    {/* Program Detail */}
                    <Col md="6">
                      <Card className="h-100 dash-card">
                        <Card.Header className="bg-white border-0 d-flex flex-wrap align-items-center justify-content-between gap-3 pt-4 px-4">
                          <div className="d-flex align-items-center">
                            <span className="dash-icon-circle bg-soft-primary me-2">
                              <MdMenuBook />
                            </span>
                            <h5 className="card-title mb-0">Program Detail</h5>
                          </div>

                          {/* Day dropdown — fully driven by programDaysList from the API */}
                          <div className="day-select-wrap">
                            <select
                              className="form-select rounded-3 border"
                              value={activeDay}
                              onChange={(e) => setActiveDay(e.target.value)}
                              disabled={
                                daysLoading || programDaysList.length === 0
                              }
                            >
                              {programDaysList.length === 0 && (
                                <option value="">
                                  {daysLoading
                                    ? "Loading..."
                                    : "No days available"}
                                </option>
                              )}
                              {programDaysList.map((day) => (
                                <option key={day.id} value={day.id}>
                                  {day.daydisplay}
                                </option>
                              ))}
                            </select>
                            {sessionsLoading ? (
                              <span className="day-select-loading">
                                <Oval
                                  height="14"
                                  width="14"
                                  color="#ffffff"
                                  secondaryColor="#ffffff"
                                />
                              </span>
                            ) : (
                              <span className="day-select-icon">
                                <MdKeyboardArrowDown size={18} />
                              </span>
                            )}
                          </div>
                        </Card.Header>
                        <Card.Body className="px-4 pb-4 pt-3">
                          {/* <div className="dash-field-label mb-3">
                            {activeDayData.label}
                            {activeDayData.date
                              ? ` · ${activeDayData.date}`
                              : ""}
                          </div> */}
                          {(sessionsLoading || daysLoading) && (
                            <div className="d-flex justify-content-center py-3">
                              <Oval
                                height="24"
                                width="24"
                                color="#4fa94d"
                                secondaryColor="#4fa94d"
                              />
                            </div>
                          )}
                          {!sessionsLoading &&
                            !daysLoading &&
                            (activeDayData.sessions || []).length === 0 && (
                              <div className="dash-field-label py-3">
                                No sessions found for this day.
                              </div>
                            )}
                          {!sessionsLoading &&
                            !daysLoading &&
                            (activeDayData.sessions || [])
                              .filter(
                                (s, idx, arr) =>
                                  !s.lesson_plan_id ||
                                  idx ===
                                  arr.findIndex(
                                    (x) =>
                                      x.lesson_plan_id === s.lesson_plan_id,
                                  ),
                              )
                              .map((session, idx) => (
                                <div
                                  className={`day-session-row day-session-row--day-${programDaysList.findIndex(
                                    (d) => d.id === activeDayData.id,
                                  ) + 1
                                    }`}
                                  key={idx}
                                >
                                  <div className="day-session-row-left">
                                    <div>
                                      <div className="day-session-topic">
                                        {session.title}
                                      </div>
                                      <div className="day-session-meta">
                                        {session.unit}
                                      </div>
                                      <div className="day-session-meta">
                                        Lead: {session.lead}
                                        {/* {session.time_display
                                          ? ` ·${formatTime(activeDayData.sessions[0].start_time)} - ${formatTime(activeDayData.sessions[activeDayData.sessions.length - 1].end_time)}`
                                          : ""} */}
                                      </div>
                                      {/* {session.description && (
                                        <div className="day-session-desc">
                                          {session.description}
                                        </div>
                                      )} */}
                                    </div>
                                  </div>
                                  <span className="day-session-duration">
                                    {`${formatTime(activeDayData.sessions[0].start_time)} - ${formatTime(activeDayData.sessions[activeDayData.sessions.length - 1].end_time)}`}
                                  </span>
                                </div>
                              ))}
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Today's Schedule */}
                    <Col md="6">
                      <Card className="h-100 dash-card">
                        <Card.Header className="bg-white border-0 d-flex align-items-center pt-4 px-4">
                          <span className="dash-icon-circle bg-soft-info me-2">
                            <MdCalendarToday />
                          </span>
                          <h5 className="card-title mb-0">Schedule</h5>
                        </Card.Header>
                        <Card.Body className="px-4 pb-3 pt-3">
                          <div className="dash-timeline">
                            {(activeDayData.sessions || [])?.length > 0 ? (
                              (activeDayData.sessions || []).map(
                                (item, idx) => {
                                  console.log(item);
                                  return (
                                    <div
                                      className="dash-timeline-item"
                                      key={idx}
                                    >
                                      <span
                                        className={`dash-timeline-dot dash-timeline-dot--${item.mode === "Online"
                                          ? "online"
                                          : "inperson"
                                          }`}
                                      ></span>
                                      <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                          <div className="dash-field-label">
                                            {item.time_display || item.time}
                                          </div>
                                          <div className="dash-field-value">
                                            {item.subunit_desc}
                                          </div>
                                          <div className="dash-field-value">
                                            {item.description}
                                          </div>
                                        </div>
                                        <Badge
                                          bg={
                                            modeVariant[item.mode] ||
                                            "secondary"
                                          }
                                          className="ms-2 flex-shrink-0"
                                        >
                                          {item.mode}
                                        </Badge>
                                      </div>
                                    </div>
                                  );
                                },
                              )
                            ) : (
                              <div className="dash-field-label py-3">
                                No Today's Schedule found for this day.
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </section>
                <section className="container mb-5" id="announcements">
                  <Card className="h-100 dash-card">
                    <Card.Header className="bg-white border-0 d-flex align-items-center pt-4 px-4">
                      <span className="dash-icon-circle bg-soft-warning me-2">
                        <MdCampaign />
                      </span>
                      <h5 className="card-title mb-0">Announcements</h5>
                    </Card.Header>
                    <Card.Body className="px-2 pb-3 pt-2">
                      {announcementsList?.length > 0 ? (
                        <div className="dash-announcement-list">
                          {announcementsList.map((item, idx) => (
                            <div className="dash-announcement-item" key={idx}>
                              <div className="d-flex align-items-start">
                                <span className="dash-announcement-dot me-3"></span>
                                <span className="dash-field-value">
                                  {item.title}
                                </span>
                              </div>
                              <span className="dash-field-label ms-2 flex-shrink-0">
                                {item.date}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="dash-announcement-item">
                          <span className="dash-field-label ms-2 flex-shrink-0">
                            No announcement found
                          </span>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </section>
                {/* ============================================================
                      SESSION RESOURCES  ·  CERTIFICATE & RELIEVING LETTER
                  ============================================================ */}
                <section className="container mb-5" id="resources-certificates">
                  <Row className="g-4">
                    {/* Session Resources */}
                    <Col md="6">
                      <Card className="h-100 dash-card">
                        <Card.Header className="bg-white border-0 d-flex align-items-center pt-4 px-4">
                          <span className="dash-icon-circle bg-soft-info me-2">
                            <MdVideoLibrary />
                          </span>
                          <h5 className="card-title mb-0">Session Resources</h5>
                        </Card.Header>
                        <Card.Body className="px-2 pb-3 pt-2">
                          {resourcesLoading && (
                            <div className="d-flex justify-content-center py-3">
                              <Oval
                                height="24"
                                width="24"
                                color="#4fa94d"
                                secondaryColor="#4fa94d"
                              />
                            </div>
                          )}
                          {!resourcesLoading &&
                            (!sessionResources ||
                              Object.keys(sessionResources).length === 0) && (
                              <div className="dash-field-label py-3 px-2">
                                No resources found for this day.
                              </div>
                            )}
                          {!resourcesLoading &&
                            sessionResources &&
                            Object.keys(sessionResources).length > 0 && (
                              <>
                                <div className="resource-row">
                                  <div className="resource-row-left">
                                    <span className="resource-icon">📄</span>

                                    <div>
                                      <div className="resource-title">
                                        Lesson Plan PDF
                                      </div>
                                      <div className="resource-sub">
                                        {sessionResources?.planned_on
                                          ? new Date(
                                            sessionResources.planned_on,
                                          ).toLocaleDateString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })
                                          : ""}
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className="resource-btn"
                                    onClick={() =>
                                      handleResourceAction({
                                        type: "pdf",
                                        title: "Lesson Plan PDF",
                                        url: sessionResources?.lesson_pdf_path,
                                        action: "View PDF",
                                        icon: "📄",
                                        planned_on:
                                          sessionResources?.planned_on,
                                      })
                                    }
                                    disabled={
                                      !sessionResources?.lesson_pdf_path
                                    }
                                  >
                                    {sessionResources?.lesson_pdf_path
                                      ? "View PDF"
                                      : "Not Available"}
                                  </button>
                                </div>

                                <div className="resource-row">
                                  <div className="resource-row-left">
                                    <span className="resource-icon">🎥</span>

                                    <div>
                                      <div className="resource-title">
                                        Session Recording
                                      </div>
                                      <div className="resource-sub">
                                        {sessionResources?.planned_on
                                          ? new Date(
                                            sessionResources.planned_on,
                                          ).toLocaleDateString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })
                                          : ""}
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className="resource-btn"
                                    onClick={() =>
                                      handleResourceAction({
                                        type: "video",
                                        title: "Session Recording",
                                        url: sessionResources?.session_recording,
                                        action: "Watch",
                                        icon: "🎥",
                                        planned_on:
                                          sessionResources?.planned_on,
                                      })
                                    }
                                    disabled={
                                      !sessionResources?.session_recording
                                    }
                                  >
                                    {sessionResources?.session_recording
                                      ? "Watch"
                                      : "Not Available"}
                                  </button>
                                </div>

                                <div className="resource-row">
                                  <div className="resource-row-left">
                                    <span className="resource-icon">📦</span>

                                    <div>
                                      <div className="resource-title">
                                        Resources
                                      </div>
                                      <div className="resource-sub">
                                        {sessionResources?.planned_on
                                          ? new Date(
                                            sessionResources.planned_on,
                                          ).toLocaleDateString("en-IN", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })
                                          : ""}
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    className="resource-btn"
                                    onClick={() =>
                                      handleResourceAction({
                                        type: "download",
                                        title: "Resources",
                                        url: sessionResources?.resources_zip,
                                        action: "Download",
                                        icon: "📦",
                                        planned_on:
                                          sessionResources?.planned_on,
                                      })
                                    }
                                    disabled={!sessionResources?.resources_zip}
                                  >
                                    {sessionResources?.resources_zip
                                      ? "Download"
                                      : "Not Available"}
                                  </button>
                                </div>
                              </>
                            )}
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Certificate & Relieving Letter */}
                    <Col md="6">
                      <Card className="h-100 dash-card">
                        <Card.Header className="bg-white border-0 d-flex align-items-center pt-4 px-4">
                          <span className="dash-icon-circle bg-soft-success me-2">
                            <MdWorkspacePremium />
                          </span>
                          <h5 className="card-title mb-0">
                            Certificate &amp; Relieving Letter
                          </h5>
                        </Card.Header>
                        <Card.Body className="px-4 pb-4 pt-2">
                          <div className="cert-row">
                            <div className="cert-row-left">
                              <div>
                                <div className="cert-title">
                                  {documentsInfo.certificate.label}
                                </div>
                                <span className="cert-issued-badge">
                                  {documentsInfo.certificate.status ||
                                    "Not Issued"}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="cert-download-btn"
                              onClick={() =>
                                handleDocumentView(
                                  documentsInfo.certificate,
                                  "certificate",
                                )
                              }
                              disabled={
                                documentsInfo.certificate.status !== "Issued"
                              }
                            >
                              View / Download Certificate
                            </button>
                          </div>
                          {Number(is_relieving_letter_enabled) === 1 && (
                            <div className="cert-row">
                              <div className="cert-row-left">
                                <div>
                                  <div className="cert-title">
                                    {documentsInfo.relievingLetter.label}
                                  </div>

                                  <span className="cert-issued-badge">
                                    {documentsInfo.relievingLetter.status || "Not Issued"}
                                  </span>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="cert-download-btn"
                                onClick={() =>
                                  handleDocumentView(
                                    documentsInfo.relievingLetter,
                                    "letter"
                                  )
                                }
                                disabled={
                                  documentsInfo.relievingLetter.status !== "Issued"
                                }
                              >
                                View / Download Relieving Letter
                              </button>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </section>
              </Card.Body>
            )}
          </Col>
        </Row>
      </Container>

      <Modal
        show={modalShow}
        onHide={closeModal}
        centered
        size="lg"
        dialogClassName="preview-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title className="preview-modal-title">
            {modalContent.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="preview-modal-body">
          {modalContent.kind === "video" && modalContent.url && (
            <div className="preview-video-wrap">
              <iframe
                src={modalContent.url}
                title={modalContent.title || "Video"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                frameBorder="0"
              />
            </div>
          )}
          {modalContent.kind === "pdf" && modalContent.url && (
            <div className="preview-pdf-wrap">
              <iframe
                src={modalContent.url}
                title={modalContent.title || "Document"}
                frameBorder="0"
              />
            </div>
          )}
          {modalContent.kind === "certificate" && (
            <div className="preview-doc-wrap">
              <CertificatePreview
                ref={previewRef}
                participantInfo={participantInfo}
                programInfo={programInfo}
                documentsInfo={documentsInfo}
              />
            </div>
          )}
          {modalContent.kind === "letter" && (
            <div className="preview-doc-wrap">
              <LetterPreview
                ref={previewRef}
                participantInfo={participantInfo}
                programInfo={programInfo}
                documentsInfo={documentsInfo}
              />
            </div>
          )}
        </Modal.Body>
        {modalContent.kind === "pdf" && (
          <Modal.Footer>
            <Button variant="outline-secondary" size="sm" onClick={closeModal}>
              Close
            </Button>
            <Button
              size="sm"
              className="preview-modal-download-btn"
              onClick={() =>
                triggerDownload(
                  modalContent.downloadUrl,
                  modalContent.downloadName,
                )
              }
              disabled={!modalContent.downloadUrl}
            >
              <MdFileDownload
                style={{ marginRight: 6, verticalAlign: "text-bottom" }}
              />
              Download
            </Button>
          </Modal.Footer>
        )}
        {(modalContent.kind === "certificate" ||
          modalContent.kind === "letter") && (
            <Modal.Footer className="preview-modal-footer--dummy">
              <span className="preview-modal-footer-note">
                Sample preview — connect a real file to enable a true PDF
                download.
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={closeModal}
                >
                  Close
                </Button>
                <Button
                  size="sm"
                  className="preview-modal-download-btn"
                  onClick={() => printPreview(modalContent.title)}
                >
                  <MdFileDownload
                    style={{ marginRight: 6, verticalAlign: "text-bottom" }}
                  />
                  Download
                </Button>
              </div>
            </Modal.Footer>
          )}
      </Modal>
    </div>
  );
};

export default Admin;
